import { AlertTriangle, Cloud, Download, Film, HardDrive, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Spinner } from '../components/ui/spinner';
import { useToast } from '../components/ui/toast';
import {
  type RecordingListItem,
  type RecordingsUsage,
  useDeleteRecording,
  useFetchRecordingUrl,
  useRecordings,
  useRecordingsUsage,
} from '../lib/api/live';
import { fmtDateTime, fmtRelative } from '../lib/date';

/**
 * R2 recordings archive. Cursor pagination — `pages` accumulates
 * `beforeCreatedAt` values as the user clicks "Load older", so the
 * full session-long view stays in memory without re-fetching.
 *
 * Download links are minted on click via a short-lived presigned R2
 * URL — we don't cache the URL because R2 imposes its own expiry and
 * minting is cheap (signed in-process, no extra round trip).
 */
export default function LiveContributionsArchivePage(): JSX.Element {
  const [pages, setPages] = useState<{ before?: string }[]>([{}]);
  const lastBefore = pages[pages.length - 1]?.before;
  const list = useRecordings({ beforeCreatedAt: lastBefore, limit: 50 });
  const usage = useRecordingsUsage();

  return (
    <>
      <Topbar title="Recordings archive" />
      <PageHeader
        title="Recordings archive"
        subtitle="Every contribution that's been uploaded to R2. Newest first. Click Open to play in a new tab — right-click the video to save the file."
      />
      <div className="space-y-4 px-6 pb-10">
        {usage.data ? <StorageStats usage={usage.data} /> : null}

        {!list.data && list.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        )}

        {list.data && list.data.items.length === 0 && pages.length === 1 && (
          <Empty
            icon={Film}
            title="No recordings yet"
            description="Recordings appear here once SRS finishes a contribution and the uploader cron ships the MP4 to R2."
          />
        )}

        {list.data?.items.map((rec) => <RecordingRow key={rec.id} recording={rec} />)}

        {list.data?.nextCursor && (
          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              onClick={() =>
                setPages((p) => [...p, { before: list.data!.nextCursor ?? undefined }])
              }
              disabled={list.isFetching}
            >
              {list.isFetching ? 'Loading…' : 'Load older'}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

function RecordingRow({ recording }: { recording: RecordingListItem }): JSX.Element {
  const fetchUrl = useFetchRecordingUrl();
  const del = useDeleteRecording();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const onDownload = async () => {
    try {
      const { url } = await fetchUrl.mutateAsync(recording.id);
      // Open in a new tab — the browser handles the MP4 download
      // (Content-Disposition isn't set by R2 by default, so this is a
      // play-in-tab; the user can right-click → "Save video as…").
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.showToast({
        title: 'Could not get download URL',
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  };

  const onDelete = async () => {
    try {
      await del.mutateAsync(recording.id);
      toast.showToast({ title: 'Recording deleted', variant: 'success' });
      setConfirming(false);
    } catch (err) {
      toast.showToast({
        title: 'Could not delete recording',
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  };

  const correspondent = recording.correspondent;
  const corrLabel = correspondent
    ? correspondent.name ?? correspondent.email
    : 'Unknown correspondent';

  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-surface-muted">
          <Film className="h-5 w-5 text-ink-subtle" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate font-medium text-ink">
              {recording.title ?? '(untitled contribution)'}
            </span>
            <span className="font-mono text-[11px] text-ink-subtle">
              {fmtRelative(recording.createdAt)}
            </span>
          </div>
          <div className="mt-0.5 truncate text-xs text-ink-muted">
            {corrLabel}
            {recording.sizeBytes != null && <> · {formatBytes(recording.sizeBytes)}</>}
            {recording.uploadedAt && <> · uploaded {fmtDateTime(recording.uploadedAt)}</>}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onDownload}
          disabled={fetchUrl.isPending}
          title="Plays in a new tab. Right-click the video → Save video as… to download to disk."
        >
          <Download className="h-3.5 w-3.5" />
          {fetchUrl.isPending ? 'Linking…' : 'Open'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirming(true)}
          disabled={del.isPending}
          title="Delete this recording permanently"
          aria-label="Delete recording"
        >
          <Trash2 className="h-3.5 w-3.5 text-danger" />
        </Button>
      </CardContent>

      <Dialog open={confirming} onOpenChange={(o) => !o && setConfirming(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete recording?</DialogTitle>
            <DialogDescription>
              Permanently removes “{recording.title ?? 'this contribution'}” from R2. This can't
              be undone — the file is gone for good.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={del.isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={onDelete} disabled={del.isPending}>
              {del.isPending ? <Spinner className="mr-2 size-4" /> : <Trash2 className="mr-2 size-4" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * Storage usage panel — twin progress bars (local disk + R2 bucket)
 * with their respective retention caps. Refreshes every 30 s so
 * operators see the impact of a cron sweep before the page reload.
 *
 * Colour shifts:
 *   <60% → neutral, business as usual
 *   60-85% → amber, "starting to fill"
 *   ≥85%  → red, "next sweep will probably evict something"
 *
 * The local card also highlights pending-upload pressure separately:
 * a non-zero `pendingUpload` means the uploader couldn't ship those
 * to R2 (network/credentials), so they'll be the first thing evicted
 * if disk pressure builds up.
 */
function StorageStats({ usage }: { usage: RecordingsUsage }): JSX.Element {
  const localBytePct = pct(usage.local.bytes, usage.local.maxBytes);
  const localCountPct = pct(usage.local.count, usage.local.maxCount);
  const localPct = Math.max(localBytePct, localCountPct);
  const r2Pct = pct(usage.r2.bytes, usage.r2.maxBytes);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Card>
        <CardContent className="space-y-2 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <HardDrive className="size-4 text-ink-subtle" />
              Local disk
            </div>
            <span className="font-mono text-xs text-ink-muted">
              {formatBytes(usage.local.bytes)} / {formatBytes(usage.local.maxBytes)} ·{' '}
              {usage.local.count} / {usage.local.maxCount} files
            </span>
          </div>
          <UsageBar pct={localPct} />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-muted">
            <span>
              {usage.local.bytes === 0
                ? 'Empty'
                : `${localPct.toFixed(0)}% of byte cap, ${localCountPct.toFixed(0)}% of file cap`}
            </span>
            {usage.local.pendingUpload > 0 ? (
              <span className="inline-flex items-center gap-1 text-warning">
                <AlertTriangle className="size-3" />
                {usage.local.pendingUpload} pending upload
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Cloud className="size-4 text-ink-subtle" />
              R2 archive
            </div>
            <span className="font-mono text-xs text-ink-muted">
              {usage.r2.configured ? (
                <>
                  {formatBytes(usage.r2.bytes)} / {formatBytes(usage.r2.maxBytes)} ·{' '}
                  {usage.r2.count} files
                </>
              ) : (
                'not configured'
              )}
            </span>
          </div>
          {usage.r2.configured ? (
            <>
              <UsageBar pct={r2Pct} />
              <div className="text-[11px] text-ink-muted">
                {usage.r2.bytes === 0
                  ? 'Empty — recordings will land here once a broadcast finishes.'
                  : `${r2Pct.toFixed(0)}% of cap. At cap, oldest recordings are evicted first.`}
              </div>
            </>
          ) : (
            <div className="text-[11px] text-ink-muted">
              Set R2_RECORDINGS_BUCKET + the R2 credentials in the API env to enable archival.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UsageBar({ pct }: { pct: number }): JSX.Element {
  // Tailwind doesn't allow dynamic-width via interpolated class names;
  // use an inline style for the fill. Clamp to 100 so a stale snapshot
  // taken mid-sweep doesn't render past the track.
  const width = `${Math.min(100, Math.max(0, pct)).toFixed(1)}%`;
  const tone =
    pct >= 85 ? 'bg-danger' : pct >= 60 ? 'bg-warning' : 'bg-brand';
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-surface-muted">
      <div className={`h-full rounded-full ${tone} transition-[width]`} style={{ width }} />
    </div>
  );
}

function pct(value: number, max: number): number {
  if (max <= 0) return 0;
  return (value / max) * 100;
}
